import React, { useState } from "react";
import { Box, Button, Collapse } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { FONT_SIZE } from "../../constants/fontSizes";

/**
 * 下部ボタンで開閉する共通セクション
 *
 * - デフォルト：下部に開閉ボタンを表示
 * - renderButton指定時：ボタンを任意の位置に配置可能
 *
 * renderButton: ({ buttonUI, expanded, toggle }) => ReactNode
 * - buttonUI: デフォルトのボタンUI
 * - expanded: 開閉状態
 * - toggle: 開閉を切り替える関数（自作ボタンではこれを呼ぶ）
 */

const ExpandableSection = ({
  collapsedContent,
  expandedContent,
  summaryText = "もっと見る",
  closeText = "閉じる",
  defaultExpanded = false,
  showButton = true,
  expandedSpacing = 0,
  renderButton,
  buttonSx,
  buttonProps = {},
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const toggle = () => setExpanded((prev) => !prev);

  // onClickとsxは内部制御とマージするため分離
  const {
    onClick: externalOnClick,
    sx: buttonPropsSx,
    ...restButtonProps
  } = buttonProps;

  // 外部onClickとtoggleを両方実行
  const handleClick = (e) => {
    externalOnClick?.(e);
    toggle();
  };

  // ボタン共通UI
  const buttonUI = (
    <Button
      {...restButtonProps}
      fullWidth
      variant="outlined"
      onClick={handleClick}
      aria-expanded={expanded}
      sx={[
        {
          borderRadius: 999,
          py: 1,
          fontSize: FONT_SIZE.SM,
          textTransform: "none",
          color: "#6C73DD",
          borderColor: "#6C73DD",
          "&:hover": {
            backgroundColor: "rgba(108, 115, 221, 0.08)",
            borderColor: "#6C73DD",
          },
        },
        buttonSx,
        buttonPropsSx,
      ]}
    >
      <Box
        sx={{
          width: "100%",
          position: "relative",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box component="span" sx={{ fontWeight: "bold" }}>
          {expanded ? closeText : summaryText}
        </Box>

        <ExpandMoreIcon
          sx={{
            position: "absolute",
            right: 0,
            color: "#6C73DD",
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "0.2s",
          }}
        />
      </Box>
    </Button>
  );

  return (
    <Box>
      {/* renderButton：呼び出し側で配置 */}
      {showButton && renderButton?.({ buttonUI, expanded, toggle })}

      {/* 常に表示される部分 */}
      {collapsedContent}

      {/* 展開時に追加で表示される部分 */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ mt: expandedSpacing }}>{expandedContent}</Box>
      </Collapse>

      {/* デフォルト（資格欄など） */}
      {showButton && !renderButton && <Box mt={1}>{buttonUI}</Box>}
    </Box>
  );
};

export default ExpandableSection;
